import type { Ref } from 'vue'
import Vue, { computed } from 'vue'

import {
  WithRelation,
  Activity,
  ActivityGroup,
  Room,
  RoomRelation,
  RelationType,
  ActivityRelationSettings,
  ActivityType,
  ActivityStepType,
} from '@/types'
import { getCurrentInstance, sortByPosition, withRelation } from '@/util'
import {
  RelationsContainerWithGroups,
  ActivityLinkResult,
} from '@/components/ActivityLink/ActivityLink.types'
import useLinkActivity from '@/components/ActivityLink/use/useActivityLink'
import {
  ActivityLinkActivityGroupTarget,
  ActivityLinkActivityTarget,
} from '@/components/ActivityLink/ActivityLinkTarget'
import { Relation } from '@/components/ActivityLink/Relation'

import { useRoomService } from './services'
import useDialogify from './useDialogify'

type Target = ActivityLinkActivityTarget | ActivityLinkActivityGroupTarget

export default function useRoom(room: Ref<Room>) {
  const instance = getCurrentInstance()
  const { service } = useRoomService()
  const { dialogify } = useDialogify()
  const h = instance.proxy.$createElement

  const activities = computed<WithRelation<Activity>[]>(() => {
    const sorted = sortByPosition(room.value.activities)
    return sorted.map((item) => withRelation(item, 'activity'))
  })

  const activityGroups = computed<WithRelation<ActivityGroup>[]>(() => {
    for (const roomActivityGroup of room.value.activityGroups) {
      roomActivityGroup.activityGroup.activities = sortByPosition(
        roomActivityGroup.activityGroup.activities
      )
    }
    const sorted = sortByPosition(room.value.activityGroups)
    return sorted.map((item) => withRelation(item, 'activityGroup'))
  })

  const container = computed<
    RelationsContainerWithGroups<Target, RoomRelation>
  >(() => {
    return {
      id: room.value.id,
      relations: room.value.relations.map(
        (relation) =>
          new Relation(
            relation.id,
            relation.left.type === 'Activity'
              ? new ActivityLinkActivityTarget(relation.left.id)
              : new ActivityLinkActivityGroupTarget(relation.left.id),
            relation.right.type === 'Activity'
              ? new ActivityLinkActivityTarget(relation.right.id)
              : new ActivityLinkActivityGroupTarget(relation.right.id),
            relation.type,
            relation.settings
          )
      ),
      activities: room.value.activities.map((roomActivity) => {
        return {
          id: roomActivity.id,
          name: roomActivity.activity.name,
          type: roomActivity.activity.type,
        }
      }),
      groups: room.value.activityGroups.map((roomActivityGroup) => {
        return {
          id: roomActivityGroup.id,
          name: roomActivityGroup.activityGroup.name,
        }
      }),
      link(payload: {
        left: Target
        right: Target
        type: RelationType
        settings: ActivityRelationSettings
      }) {
        return service.link({
          roomId: room.value.id,
          left: { type: 'Activity', id: payload.left.id },
          right: {
            type:
              payload.right instanceof ActivityLinkActivityTarget
                ? 'Activity'
                : 'Group',
            id: payload.right.id,
          },
          type: payload.type,
          settings: payload.settings,
        })
      },
      linkBulk(
        payload: {
          left: Target
          right: Target
          type: RelationType
          settings?: ActivityRelationSettings
        }[]
      ) {
        return service.linkBulk(
          payload.map((item) => ({
            roomId: room.value.id,
            left: { type: 'Activity', id: item.left.id },
            right: {
              type:
                item.right instanceof ActivityLinkActivityTarget
                  ? 'Activity'
                  : 'Group',
              id: item.right.id,
            },
            type: item.type,
            settings: item.settings,
          }))
        )
      },
      update(payload: {
        id: number
        right: Target | null
        settings: ActivityRelationSettings
      }) {
        return service.updateLink(payload.id, {
          ...(payload.right === null
            ? {
                rightActivityId: undefined,
                rightActivityGroupId: undefined,
                settings: {},
              }
            : payload.right instanceof ActivityLinkActivityTarget
            ? { rightActivityId: payload.right.id }
            : { rightActivityGroupId: payload.right.id }),
          settings: payload.settings,
        })
      },
      async unlink(id: number) {
        await service.unlink(id)
      },
    }
  })
  const { link: originalLink } = useLinkActivity(container)

  async function link(activity: WithRelation<Activity>) {
    if (activity.type === ActivityType.Poll) {
      const play =
        activity.steps.find((step) => step.type === ActivityStepType.Play) ??
        null
      // @ts-expect-error TODO
      if (play !== null && play.settings.trw === true) {
        const { default: options } = await import(
          '../components/ActivityLink/ActivityLinkTrw.vue'
        )
        const Component = Vue.extend({ extends: options })
        return new Promise<ActivityLinkResult<RoomRelation>[] | null>(
          (resolve) => {
            const { hide } = dialogify(
              [
                h(Component, {
                  props: {
                    target: new ActivityLinkActivityTarget(
                      activity.relation.id
                    ),
                    container: container.value,
                  },
                  on: {
                    cancel: () => {
                      hide()
                      resolve(null)
                    },
                    success: (results: ActivityLinkResult<RoomRelation>[]) => {
                      hide()
                      resolve(results)
                    },
                  },
                }),
              ],
              { title: 'Link TRW Blocks', size: 'sm' }
            )
          }
        )
      }
    }

    return originalLink(activity)
  }

  return {
    activities,
    activityGroups,
    link,
  }
}
